import {
  ComponentType,
  Overlay,
  OverlayConfig,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  inject,
  Injectable,
  Injector,
  TemplateRef,
} from '@angular/core';
import { SideDialogContainer } from './side-dialog-container.component';
import { SideDialogOptions } from './side-dialog-options.class';
import { SideDialogRef } from './side-dialog-ref.class';
import { SIDE_DIALOG_DATA } from './side-dialog.injectors';

@Injectable({
  providedIn: 'root',
})
export class SideDialogService {
  private _overlay: OverlayRef;
  private _sideDialogRef: SideDialogRef<any, any>;
  private overlay: Overlay = inject(Overlay);
  private injector: Injector = inject(Injector);

  public open<R = unknown, T = unknown>(
    component: ComponentType<T> | TemplateRef<T>,
    options?: SideDialogOptions
  ): SideDialogRef<R, T> {
    if (!!this._overlay) {
      this.close();
    }

    options = options ?? {};

    options.autoFocus = options.autoFocus ?? 'first-tabbable';

    this._overlay = this.createOverlay(options);

    this._sideDialogRef = new SideDialogRef<R, T>(this._overlay, options);

    const injector = Injector.create({
      providers: [
        { provide: SIDE_DIALOG_DATA, useValue: options.data },
        { provide: SideDialogOptions, useValue: options },
        { provide: SideDialogRef, useValue: this._sideDialogRef },
      ],
      parent: this.injector,
    });

    const container = new ComponentPortal(SideDialogContainer, null, injector);

    const containerRef = this._overlay.attach(container);

    containerRef.instance;

    if (component instanceof TemplateRef) {
      let context: any = {
        $implicit: options.data,
        dialogRef: this._sideDialogRef,
      };

      if (options.templateContext) {
        context = {
          ...context,
          ...(typeof options.templateContext === 'function'
            ? options.templateContext()
            : options.templateContext),
        };
      }

      containerRef.instance.attachTemplatePortal(
        new TemplatePortal<T>(component, null, context, injector)
      );
    } else {
      const compRef = containerRef.instance.attachComponentPortal<T>(
        new ComponentPortal(component, null, injector)
      );

      (this._sideDialogRef as { componentInstance: T }).componentInstance =
        compRef.instance;
      (this._sideDialogRef as { componentRef: ComponentRef<T> }).componentRef =
        compRef;
    }

    return this._sideDialogRef;
  }

  public close(): void {
    this._overlay?.detach();
    this._overlay?.dispose();
    this._overlay = undefined;

    if (!!this._sideDialogRef) {
      this._sideDialogRef.closed.emit();
      this._sideDialogRef = undefined;
    }
  }

  private createOverlay(options: SideDialogOptions): OverlayRef {
    const panelClass = ['side-dialog-panel'];

    if (!!options.panelClass) {
      if (Array.isArray(options.panelClass)) {
        panelClass.push(...options.panelClass);
      } else {
        panelClass.push(options.panelClass);
      }
    }

    const config = new OverlayConfig({
      hasBackdrop: true,
      backdropClass: ['side-dialog-backdrop'],
      panelClass: panelClass,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .global()
        .right('0')
        .top('0')
        .bottom('0'),
      width: options.width ?? '1000px',
      height: '100%',
      disposeOnNavigation: true,
    });

    return this.overlay.create(config);
  }
}
